import { motion } from "framer-motion";

import { Button } from "@/atoms/button/Button";
import { Icon } from "@/atoms/icon/Icon";
import Book from "@/atoms/icon/icons/personalisation_book.svg";
import Heart from "@/atoms/icon/icons/personalisation_heart.svg";
import Users from "@/atoms/icon/icons/personalisation_users.svg";
import { TextButton } from "@/atoms/text-button/TextButton";
import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { usePlatform } from "hooks/usePlatform";
import { getPossessiveForm } from "utils/functions";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";

interface PersonalisationAdvisorBenefitsProps {
  onContinue: () => void;
  onSkip: () => void;
}

export const PersonalisationAdvisorBenefits = ({
  onContinue,
  onSkip,
}: PersonalisationAdvisorBenefitsProps) => {
  const { platform } = usePlatform();

  const cards = [
    {
      icon: Users,
      text: "Built on insights from 2M+ real patient plans.",
    },
    {
      icon: Book,
      text: `Trained on ${getPossessiveForm(
        platform?.name
      )} actual treatments, not generic advice.`,
    },
    {
      icon: Heart,
      text: "Your data stays private and encrypted.",
    },
  ];

  return (
    <PersonalisationScreenLayout
      footer={
        <>
          <Button style="primary" size="large" fullWidth onClick={onContinue}>
            Meet your advisor
          </Button>
          <TextButton variant="secondary" onClick={onSkip}>
            Skip for now
          </TextButton>
        </>
      }
      contentClassName="pt-[30%]"
    >
      <PersonalisationHeading size="sm">
        Welcome.
        <br /> <br />
        Here&apos;s why your AI treatment advisor helps you reach your goals
        faster:
      </PersonalisationHeading>

      <div className="flex flex-col gap-4">
        {/* Card animations are delayed to sequence after screen load animation (0.1s-0.6s) */}
        {cards.map((card, index) => (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: ANIMATION_DURATION,
              delay: 0.6 + index * 0.25,
            }}
            className="flex flex-row items-center gap-3 rounded-2xl bg-white/60 p-4 shadow-subtle backdrop-blur-sm"
            key={index}
          >
            <div className="size-8 shrink-0">
              <Icon svg={card.icon} size="medium" color="brandColor" />
            </div>
            <div className="font-sans text-body1 text-primary-neutral">
              {card.text}
            </div>
          </motion.div>
        ))}
      </div>
    </PersonalisationScreenLayout>
  );
};
